<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: "BaseButton",
  props: {
    variant: {
      type: String,
      default: "contained",
      validator: (value) => ["contained", "outlined"].includes(value),
    },
    size: {
      type: String,
      default: "medium",
      validator: (value) => ["full", "large", "medium", "small"].includes(value),
    },
    color: {
      type: String,
      default: "black",
      validator: (value) => ["black", "green", "red"].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    buttonClasses() {
      return [
        "base-button",
        `base-button--${this.variant}`,
        `base-button--${this.size}`,
        `base-button--${this.color}`,
        {
          "base-button--disabled": this.disabled,
        },
      ];
    },
  },
  emits: ["click"],
};
</script>

<style scoped>
.base-button {
  border-radius: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  text-decoration: none;
  font-family: inherit;
  white-space: nowrap;
  min-width: 70px;
}

/* Variants */
.base-button--contained,
.base-button--outlined {
  border-radius: 4px;
  transition: none;
  box-shadow: none;
}

/* ===============================
   BLACK (default)
   =============================== */
.base-button--contained.base-button--black {
  color: #fff;
  background-color: #000;
  border-color: #000;
}

.base-button--contained.base-button--black:hover:not(.base-button--disabled) {
  background-color: #333;
  border-color: #333;
}

.base-button--contained.base-button--black:active:not(.base-button--disabled) {
  background-color: #505050;
  border-color: #505050;
}

.base-button--outlined.base-button--black {
  color: #000;
  background-color: #fff;
  border-color: #000;
}

.base-button--outlined.base-button--black:hover:not(.base-button--disabled) {
  background-color: rgba(0, 0, 0, 0.08);
}

.base-button--outlined.base-button--black:active:not(.base-button--disabled) {
  background-color: rgba(0, 0, 0, 0.15);
}

/* ===============================
   GREEN
   =============================== */
.base-button--contained.base-button--green {
  color: #fff;
  background-color: #43a047; /* green600 */
  border-color: #43a047;
}
.base-button--contained.base-button--green:hover:not(.base-button--disabled) {
  background-color: #4caf50;
  border-color: #4caf50;
}
.base-button--contained.base-button--green:active:not(.base-button--disabled) {
  background-color: #388e3c;
  border-color: #388e3c;
}

.base-button--outlined.base-button--green {
  color: #43a047;
  background-color: #fff;
  border-color: #43a047;
}
.base-button--outlined.base-button--green:hover:not(.base-button--disabled) {
  background-color: rgba(67, 160, 71, 0.08);
  border-color: #43a047;
}
.base-button--outlined.base-button--green:active:not(.base-button--disabled) {
  background-color: rgba(67, 160, 71, 0.15);
  border-color: #43a047;
}

/* ===============================
   RED
   =============================== */
.base-button--contained.base-button--red {
  color: #fff;
  background-color: #c62828;
  border-color: #c62828;
}

.base-button--contained.base-button--red:hover:not(.base-button--disabled) {
  background-color: #d32f2f;
  border-color: #d32f2f;
}

.base-button--contained.base-button--red:active:not(.base-button--disabled) {
  background-color: #b71c1c;
  border-color: #b71c1c;
}

.base-button--outlined.base-button--red {
  color: #c62828;
  background-color: #fff;
  border-color: #c62828;
}

.base-button--outlined.base-button--red:hover:not(.base-button--disabled) {
  background-color: rgba(198, 40, 40, 0.08);
}

.base-button--outlined.base-button--red:active:not(.base-button--disabled) {
  background-color: rgba(198, 40, 40, 0.15);
}

/* Sizes */
.base-button--full {
  width: 100%;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  height: 30px;
}

.base-button--large {
  padding: 10px 24px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  height: 30px;
}

.base-button--medium {
  padding: 7px 16px;
  font-size: 14px;
  line-height: 24px;
  font-weight: 600;
  height: 30px;
}

.base-button--small {
  padding: 3px 12px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  height: 24px;
}

/* Disabled */
.base-button--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.base-button--contained.base-button--disabled {
  background-color: #e0e0e0;
  border-color: #e0e0e0;
  color: #9e9e9e;
}

.base-button--outlined.base-button--disabled {
  background-color: #fafafa;
  border-color: #e0e0e0;
  color: #9e9e9e;
}

/* ===============================
   FOCUS
   =============================== */
.base-button:focus-visible {
  box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.2);
}
</style>
